import React, { useMemo, useState } from "react";
import { Check, Eye, MoreVertical, Search, Send, X } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const STATUS_STYLES = {
  submitted: "bg-blue-50 text-blue-700",
  "under-review": "bg-yellow-50 text-yellow-800",
  approved: "bg-green-50 text-green-700",
  rejected: "bg-red-50 text-red-700",
  "in-progress": "bg-gray-100 text-gray-700",
};

const STATUS_LABELS = {
  submitted: "Submitted",
  "under-review": "Under Review",
  approved: "Approved",
  rejected: "Rejected",
  "in-progress": "In Progress",
};

function RejectModal({ open, company, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="text-left text-lg font-semibold text-[#1D2739]">
          Reject supplier
        </h3>
        <p className="mt-1 text-left text-sm text-gray-500">
          Provide a reason for rejecting {company}.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter rejection reason"
          rows={5}
          className="mt-4 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-100"
        />
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setReason("");
              onClose();
            }}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onSubmit(reason.trim());
              setReason("");
            }}
            disabled={!reason.trim()}
            className="rounded-lg bg-[#EAF524] px-4 py-2 text-sm font-semibold text-black disabled:opacity-50"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

function ViewSubmissionModal({ open, item, onClose }) {
  if (!open || !item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 text-left shadow-xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded p-1 text-gray-400 hover:bg-gray-100"
        >
          <X className="h-5 w-5" />
        </button>
        <h3 className="text-lg font-semibold text-[#1D2739]">View Submission</h3>
        <dl className="mt-5 space-y-3 text-sm">
          <div>
            <dt className="text-gray-400">Company</dt>
            <dd className="font-medium text-[#1D2739]">{item.company}</dd>
          </div>
          <div>
            <dt className="text-gray-400">Contact</dt>
            <dd className="text-[#1D2739]">{item.contactName}</dd>
          </div>
          <div>
            <dt className="text-gray-400">Email</dt>
            <dd className="text-[#1D2739]">{item.email}</dd>
          </div>
          <div>
            <dt className="text-gray-400">Form</dt>
            <dd className="text-[#1D2739]">{item.formName}</dd>
          </div>
          <div>
            <dt className="text-gray-400">Submitted</dt>
            <dd className="text-[#1D2739]">{formatDate(item.submittedAt)}</dd>
          </div>
          <div>
            <dt className="text-gray-400">Status</dt>
            <dd>
              <span
                className={`mt-1 inline-block rounded-full px-3 py-1 text-xs font-medium ${
                  STATUS_STYLES[item.status] || "bg-gray-100 text-gray-700"
                }`}
              >
                {STATUS_LABELS[item.status] || item.status}
              </span>
            </dd>
          </div>
          {item.rejectReason ? (
            <div>
              <dt className="text-gray-400">Rejection reason</dt>
              <dd className="text-[#1D2739]">{item.rejectReason}</dd>
            </div>
          ) : null}
        </dl>
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-[#EAF524] px-8 py-2.5 text-sm font-semibold text-black"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PostRegistrationList({
  submissions,
  onApprove,
  onReject,
  onRequestInfo,
}) {
  const [query, setQuery] = useState("");
  const [menuId, setMenuId] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [rejectItem, setRejectItem] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return submissions;
    return submissions.filter(
      (s) =>
        s.company.toLowerCase().includes(q) ||
        s.contactName.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.formName.toLowerCase().includes(q)
    );
  }, [submissions, query]);

  return (
    <div className="px-6 py-6 text-left">
      <h2 className="mb-6 text-xl font-semibold text-[#1D2739]">
        Post Registration
      </h2>

      <div className="mb-6 flex items-center gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="w-[320px] rounded-lg bg-gray-100 py-2 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <button
          type="button"
          className="rounded-lg bg-blue-500 px-5 py-2 text-sm font-medium text-white hover:bg-blue-600"
        >
          Search
        </button>
      </div>

      {filtered.length === 0 ? (
        <p className="py-16 text-center text-sm text-gray-500">
          No post-registration submissions yet.
        </p>
      ) : (
        <div className="overflow-visible rounded-xl border border-gray-100">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-white text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Company</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Form</th>
                <th className="px-4 py-3 font-medium">Submitted</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60"
                >
                  <td className="px-4 py-4 font-medium text-[#1D2739]">
                    {item.company}
                  </td>
                  <td className="px-4 py-4 text-gray-600">{item.contactName}</td>
                  <td className="px-4 py-4 text-gray-600">{item.email}</td>
                  <td className="px-4 py-4 text-gray-600">{item.formName}</td>
                  <td className="px-4 py-4 text-gray-600">
                    {formatDate(item.submittedAt)}
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-medium ${
                        STATUS_STYLES[item.status] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {STATUS_LABELS[item.status] || item.status}
                    </span>
                  </td>
                  <td className="relative px-4 py-4 text-right">
                    <button
                      type="button"
                      onClick={() => setMenuId(menuId === item.id ? null : item.id)}
                      className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {menuId === item.id && (
                      <div className="absolute right-4 z-20 mt-1 w-56 rounded-lg border border-gray-100 bg-white py-1 text-left shadow-lg">
                        <button
                          type="button"
                          onClick={() => {
                            setMenuId(null);
                            setViewItem(item);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Eye className="h-4 w-4" /> View Submission
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuId(null);
                            onRequestInfo(item);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Send className="h-4 w-4" /> Request more information
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuId(null);
                            onApprove(item);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-green-700 hover:bg-green-50"
                        >
                          <Check className="h-4 w-4" /> Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuId(null);
                            setRejectItem(item);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" /> Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ViewSubmissionModal
        open={!!viewItem}
        item={viewItem}
        onClose={() => setViewItem(null)}
      />
      <RejectModal
        open={!!rejectItem}
        company={rejectItem?.company}
        onClose={() => setRejectItem(null)}
        onSubmit={(reason) => {
          onReject(rejectItem, reason);
          setRejectItem(null);
        }}
      />
    </div>
  );
}
